import {McRouteConfiguration} from '../McRouteConfiguration';
import {Map} from 'maplibre-gl';
import {Feature, FeatureCollection} from 'geojson';
import { MapContent, MapContentBase } from '../MapContent';
import {MapContentSource} from '../MapContentSource';
import {ExternalConfigRouteLayer} from './ExternalConfigRouteLayer';
import {MapTools} from '../../../../tools/MapTools';
import { LineStyle } from '../../../../models/styling';
import { Protocol } from 'pmtiles';

export class RouteConfigDetailMapContent extends MapContentBase implements MapContent {

  private readonly fallbackRouteStyle =  new LineStyle('#000000', 2, 1.0);

  private source: MapContentSource = new MapContentSource('only-source');
  private configRouteLayers: ExternalConfigRouteLayer[] = [];

  // stored for finding currently displayed features
  private visibleLayerIds = new Set<string>();

  constructor(
    configuration: McRouteConfiguration,
    configRouteFeatureCollection: FeatureCollection,
  ) {
    super();
    this.source.loadFromFeatureCollection(configRouteFeatureCollection);

    this.configRouteLayers = configuration.routes.map(route => {
      if (route.visible) {
        this.visibleLayerIds.add(route.id.toString());
      }
      return new ExternalConfigRouteLayer(
          route.id,
          route.visible,
          route.styleOverride,
          configuration.globalStyle,
          this.fallbackRouteStyle,
          configuration.subRouteItems,
          this.source,
      );
    });
  }

  load(map: Map, protocol: Protocol, zoomToFeatures: boolean) {
    console.log('Loading a map...');
    this.map = map;
    this.firstSymbolLayerId = this.getFirstSymbolLayerId();

    this.source.updateMap(this.map);
    console.log('Setting up route configuration layers...');
    this.configRouteLayers.forEach(layer => this.map.addLayer(layer.toLayerSpecification(), this.firstSymbolLayerId));

    console.log('Filtering visible features...');
    const visibleFeatures = this.filterVisibleFeatures();

    if (zoomToFeatures) {
      console.log('Zooming to visible features...');
      MapTools.zoomToFeatures(this.map, visibleFeatures);
    }
    console.log('Map loaded.');
  }

  unload() {
    this.configRouteLayers.forEach(layer => this.map.removeLayer(layer.layerId));
    this.map.removeSource(this.source.sourceId);
    this.map = null;
  }

  // TODO tk, get rid of visibleLayerIds, move it as property to each route layer
  //  update route layer when visibility / style / color overrides change
  //  in addition to direct map layer changes here;
  //  now changes would be lost on map content reload

  changeRouteVisibility(configuration: McRouteConfiguration) {
    if (this.map != null) {

      this.visibleLayerIds.clear();
      configuration.routes.forEach(route => {
        const layerId = route.id.toString();
        if (route.visible) {
          this.visibleLayerIds.add(layerId);
        }
        this.map.setLayoutProperty(
          layerId,
          'visibility',
          route.visible ? 'visible' : 'none'
        );
      });

      MapTools.zoomToFeatures(this.map, this.filterVisibleFeatures());
    }
  }

  updateLayerStyles(configuration: McRouteConfiguration) {
    if (this.map != null) {

      // 1. use global for all
      // 2. use overrides, fallback to global
      // 3. use sub-route configuration
      configuration.routes.forEach(route => {
        const layerId = route.id.toString();
        const layer = this.configRouteLayers.find(l => l.layerId === layerId);
        layer.updateGlobalStyle(configuration.globalStyle);
        this.map.setPaintProperty(layerId, 'line-color', layer.getColorExpr(route.styleOverride?.color));
        this.map.setPaintProperty(layerId, 'line-width', layer.getWidthExpr(route.styleOverride?.width));
        this.map.setPaintProperty(layerId, 'line-opacity', layer.getOpacityExpr(route.styleOverride?.opacity));
        this.map.setPaintProperty(
            layerId,
            'line-dasharray',
            layer.getLineTypeExpr(route.styleOverride?.lineType),
        );
      });
    }
  }

  private filterVisibleFeatures(): Feature[] {
    const allFeatures = this.source.features;
    if (allFeatures != null && allFeatures.length > 0) {
      return allFeatures.filter(feature => {
        const layerId = feature.properties['routeId']; // beware - returns number instead of string
        return this.visibleLayerIds.has(layerId.toString());
      });
    } else {
      return [];
    }
  }
}
